import {
	loadQuery,
	type LoadQueryOptions,
	type PreloadedQuery,
	type VariablesOf,
} from 'react-relay';
import {
	createResource,
	type ResourceStoreContext,
	type RouterContext,
	type RouterDataContext,
	type RouteResource,
	useResource,
} from 'react-resource-router';
import { type ConcreteRequest, type IEnvironment, type OperationType } from 'relay-runtime';

import { HelpHelpCenterAri, HelpPageAri } from '@atlassian/ari/help';
import { __INTERNAL_RELAY_ENVIRONMENT__DO_NOT_USE_THIS_DIRECTLY__ } from '@atlassian/relay-environment-provider/environment';

export interface RelayResourceConfig<TQuery extends OperationType> {
	cacheKey?: string;
	environment?: IEnvironment;
	options?: LoadQueryOptions;
	query: ConcreteRequest;
	variables?: VariablesOf<TQuery>;
}

function createCacheKey<TQuery extends OperationType>(
	query: ConcreteRequest,
	variables?: VariablesOf<TQuery>,
) {
	const queryId = 'cacheID' in query.params ? query.params.cacheID : query.params.id;
	return `relay-${queryId}-${query.params.name}-${JSON.stringify(variables ?? {})}`;
}

export const createRelayResource = <TQuery extends OperationType>({
	getQuery,
}: {
	getQuery: (
		routerContext: RouterContext | RouterDataContext,
		resourceStoreContext: ResourceStoreContext,
	) => RelayResourceConfig<TQuery>;
}): RouteResource<PreloadedQuery<TQuery, Record<string, unknown>>> =>
	createResource<PreloadedQuery<TQuery>>({
		type: 'RELAY_RESOURCE_TYPE',
		getKey: (routerContext: RouterContext, customContext: ResourceStoreContext) => {
			const { query, variables, cacheKey } = getQuery(routerContext, customContext);
			return cacheKey ?? createCacheKey(query, variables);
		},
		getData: (routerContext: RouterDataContext, customContext: ResourceStoreContext) => {
			const { query, variables, options } = getQuery(routerContext, customContext);
			const queryReference = loadQuery<TQuery>(
				__INTERNAL_RELAY_ENVIRONMENT__DO_NOT_USE_THIS_DIRECTLY__,
				query,
				variables ?? {},
				options,
			);
			return Promise.resolve(queryReference);
		},
	});

export const useRelayResource = <Query extends OperationType>(
	resource: RouteResource<PreloadedQuery<Query>>,
): {
        queryReference: PreloadedQuery<Query, Record<string, unknown>> | null;
    } => {
	const { data: queryReference } = useResource(resource);
	return {
		queryReference,
	};
};

const getSafeHelpCenterIdMeta = (
	helpCenterAri: string,
): { activationId: string; helpCenterId: string } | undefined => {
	try {
		if (helpCenterAri && helpCenterAri !== 'null' && HelpHelpCenterAri.check(helpCenterAri)) {
			const { activationId, helpCenterId } = HelpHelpCenterAri.parse(helpCenterAri);
			return { activationId, helpCenterId };
		}
	} catch (error) {
		// todo: add analytics
	}
};

export const getPageAri = (helpCenterAri: string, pageId: string): string => {
	const { activationId = '', helpCenterId = '' } = getSafeHelpCenterIdMeta(helpCenterAri) || {};
	return (
		HelpPageAri.create({
			activationId: activationId,
			pageId: pageId || helpCenterId,
		}).toString() || ''
	);
};
